import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard, BarChart3, LineChart, ClipboardList, Activity, FileText, Settings,
    Sparkles, Heart, Droplet, Thermometer, Scale, Wind, Brain, MessageCircle, User as UserIcon, Camera,
    MessagesSquare, ArrowRight, UploadCloud, Mail, Phone, IdCard, Lock, Trash2, Shield, Clock as ClockIcon,
    Images, Users as UsersIcon, Stethoscope, Menu,
} from "lucide-react";
import {
    LineChart as RC_Line, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar, AreaChart, Area,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DashSidebar } from "../components/DashSidebar";
import { HealthScoreHalo } from "../components/HealthScoreHalo";
import { MobileBottomNav } from "../components/MobileBottomNav";
import { MobileDrawer } from "../components/MobileDrawer";
import { OnlineDoctors } from "../components/OnlineDoctors";
import { ConsultationChat } from "../components/ConsultationChat";
import { MedicalRecords } from "../components/MedicalRecords";
import { AIValuationReport } from "../components/AIValuationReport";
import { DoctorAccessRequestPopup } from "../components/DoctorAccessRequestPopup";
import { KioskVitalsReport } from "../components/KioskVitalsReport";
import { ScansTab } from "../components/ScansTab";
import { FamilyTab } from "../components/FamilyTab";
import { ProfileSwitcher } from "../components/ProfileSwitcher";
import { ScanPhotoGuideModal, shouldShowScanGuide } from "../components/ScanPhotoGuideModal";
import { WhatsAppShareButton } from "../components/WhatsAppShareButton";

const ITEMS = [
    { id: "overview", label: "Dashboard", icon: LayoutDashboard },
    { id: "vitals", label: "Vitals", icon: Activity },
    { id: "scans", label: "Scans", icon: Images },
    { id: "family", label: "Family", icon: UsersIcon },
    { id: "consultations", label: "Consults", icon: MessagesSquare },
    { id: "settings", label: "Settings", icon: Settings },
];

const FULL_ITEMS = [
    { id: "overview", label: "Main Dashboard", icon: LayoutDashboard },
    { id: "vitals", label: "Vitals", icon: Activity },
    { id: "doctors", label: "Online Doctors", icon: MessageCircle },
    { id: "consultations", label: "Consultations", icon: MessagesSquare },
    { id: "statistics", label: "Statistics", icon: BarChart3 },
    { id: "analytics", label: "Analytics", icon: LineChart },
    { id: "scans", label: "Scans", icon: Images },
    { id: "family", label: "Family", icon: UsersIcon },
    { id: "medical-records", label: "Medical Records", icon: ClipboardList },
    { id: "ai-report", label: "AI Valuation Report", icon: Sparkles },
    { id: "reports", label: "Reports", icon: FileText },
    { id: "settings", label: "Settings", icon: Settings },
];

function VitalCard({ icon: Icon, label, value, unit, status = "Normal", color = "var(--medx-red)" }) {
    return (
        <div className="card-soft p-5" data-testid={`vital-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "rgba(247,127,0,0.12)", color }}>
                    <Icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full" style={{ background: "rgba(34,197,94,0.1)", color: "#16a34a" }}>{status}</span>
            </div>
            <div className="text-xs font-mono tracking-[0.18em] text-gray-500 uppercase">{label}</div>
            <div className="font-display text-3xl font-bold text-gray-900 mt-1">
                {value}
                {unit && <span className="text-base text-gray-400 font-medium ml-1">{unit}</span>}
            </div>
        </div>
    );
}

function EmptyState({ onUpload }) {
    return (
        <div className="card-soft p-10 text-center" data-testid="kiosk-empty-state">
            <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 grid place-items-center mb-4">
                <Sparkles className="w-7 h-7 text-[var(--medx-orange)]" />
            </div>
            <div className="font-display text-2xl font-semibold mb-2">No kiosk scan yet</div>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
                Walk up to any Medx AI Kiosk to begin — or upload a photo of your printed kiosk slip from your phone.
                An admin will review it and your Health Score, vitals and history will update automatically.
            </p>
            <button onClick={onUpload} className="btn-primary inline-flex items-center gap-2" data-testid="upload-scan-btn-empty">
                <UploadCloud className="w-4 h-4" /> Upload a scan
            </button>
        </div>
    );
}

function DoctorsOfflinePanel({ message }) {
    return (
        <div className="card-soft p-10 text-center" data-testid="doctors-offline-panel">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 grid place-items-center mb-4">
                <Stethoscope className="w-7 h-7 text-gray-400" />
            </div>
            <div className="font-display text-2xl font-semibold mb-2">Doctors are offline at the moment</div>
            <p className="text-gray-600 max-w-md mx-auto">
                {message || "Our doctors aren't accepting new consultations right now. Please check back later — your vitals and kiosk scans will be here waiting for you."}
            </p>
            <div className="mt-6 text-[11px] font-mono tracking-[0.22em] text-gray-400">
                PRIVATE BETA · ACCESS GRANTED PER ACCOUNT
            </div>
        </div>
    );
}

export default function PatientDashboard() {
    const { user, setUser, logout } = useAuth();
    const { hash } = useLocation();
    const { t } = useTranslation();
    const tab = (hash || "#overview").replace("#", "");

    const [data, setData] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [bio, setBio] = useState(user?.bio || "");
    const [savingBio, setSavingBio] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [consults, setConsults] = useState([]);
    const [active, setActive] = useState(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [dependents, setDependents] = useState([]);
    const [capabilities, setCapabilities] = useState({ can_access_doctors: false, doctors_offline_message: "Doctors are offline at the moment. Please check back later." });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [activeDependentId, setActiveDependentId] = useState(() => {
        try { return localStorage.getItem("medx_active_dep") || null; } catch { return null; }
    });

    // Keep active dependent in sync with localStorage so it survives reloads.
    useEffect(() => {
        try {
            if (activeDependentId) localStorage.setItem("medx_active_dep", activeDependentId);
            else localStorage.removeItem("medx_active_dep");
        } catch {/* ignore */}
    }, [activeDependentId]);

    const activeDependent = useMemo(
        () => (dependents || []).find((d) => d.id === activeDependentId && d.status === "approved") || null,
        [dependents, activeDependentId],
    );

    const effectiveUser = activeDependent
        ? {
            ...activeDependent,
            name: activeDependent.name,
            first_name: activeDependent.first_name,
            email: user?.email,  // dependent inherits parent's email for display only
          }
        : user;

    const activeConsultCount = useMemo(
        () => (consults || []).filter((c) => c.status === "active").length,
        [consults],
    );

    // Localized items reference (sidebar) — recomputed each render so badges track live consult count.
    const pendingDepCount = (dependents || []).filter((d) => d.status === "pending").length;
    const canAccessDoctors = !!capabilities?.can_access_doctors;
    const offlineClick = () => toast.info(capabilities?.doctors_offline_message || "Doctors are offline at the moment. Please check back later.", { duration: 5000 });
    const localizedFullItems = FULL_ITEMS.map((it) => ({
        ...it,
        label:
            it.id === "overview" ? t("dashboard.items.overview") :
            it.id === "vitals" ? t("dashboard.items.vitals") :
            it.id === "doctors" ? t("dashboard.items.doctors") :
            it.id === "consultations" ? t("dashboard.items.consultations", "Consultations") :
            it.id === "statistics" ? t("dashboard.items.statistics") :
            it.id === "analytics" ? t("dashboard.items.analytics") :
            it.id === "scans" ? t("dashboard.items.scans", "Scans") :
            it.id === "family" ? t("dashboard.items.family", "Family") :
            it.id === "medical-records" ? t("dashboard.items.medicalRecords") :
            it.id === "ai-report" ? t("dashboard.items.aiReport") :
            it.id === "reports" ? t("dashboard.items.reports") :
            it.id === "settings" ? t("dashboard.items.settings") :
            it.label,
        badge:
            it.id === "consultations" && canAccessDoctors && activeConsultCount > 0 ? activeConsultCount :
            it.id === "family" && pendingDepCount > 0 ? pendingDepCount :
            undefined,
        disabled: (it.id === "doctors" || it.id === "consultations") && !canAccessDoctors,
        disabledTitle: capabilities?.doctors_offline_message,
        onDisabledClick: offlineClick,
    }));

    const load = async (opts = {}) => {
        if (!opts.silent) setLoadingData(true);
        try {
            const params = activeDependentId ? { params: { dependent_id: activeDependentId } } : {};
            const { data } = await api.get("/me/dashboard", params);
            // Live-refresh: when a brand-new kiosk push arrives for the open
            // patient dashboard, surface a toast + animate the score so they
            // never miss a scan.
            if (!opts.silent) {
                setData(data);
            } else {
                setData((prev) => {
                    const prevTs = prev?.latest_vitals?.captured_at;
                    const nextTs = data?.latest_vitals?.captured_at;
                    if (nextTs && nextTs !== prevTs && prev) {
                        const kid = data.latest_vitals?.kiosk_id || "kiosk";
                        toast.success(`New scan arrived from ${kid} — your Health Score was updated.`, { duration: 6000 });
                    }
                    return data;
                });
            }
        } catch (e) {
            if (!opts.silent) {
                toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
            }
        } finally {
            if (!opts.silent) setLoadingData(false);
        }
    };
    const loadConsults = async () => {
        try {
            const { data } = await api.get("/consultations");
            setConsults(data);
        } catch {/* silent */}
    };
    const loadDependents = async () => {
        try {
            const { data } = await api.get("/me/dependents");
            setDependents(Array.isArray(data) ? data : []);
        } catch {/* silent */}
    };
    const loadCapabilities = async () => {
        try {
            const { data } = await api.get("/me/capabilities");
            setCapabilities(data);
        } catch {/* silent */}
    };
    useEffect(() => {
        load();
        loadConsults();
        loadDependents();
        loadCapabilities();
        // Poll both the consultation list AND the dashboard so kiosk pushes
        // and new chat messages surface without a manual refresh.
        const tConsults = setInterval(loadConsults, 5000);
        const tDash = setInterval(() => load({ silent: true }), 10000);
        return () => { clearInterval(tConsults); clearInterval(tDash); };
         
    }, [activeDependentId]);

    const openUpload = () => setUploadOpen(true);
    // Legacy "simulate" removed from UI (Iteration 13 — platform is going live). Backend endpoint
    // still exists for internal QA.

    const onPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 2_000_000) { toast.error("Pick a photo under 2MB"); return; }
        const reader = new FileReader();
        reader.onload = async () => {
            setPhotoUploading(true);
            try {
                const { data } = await api.post("/me/photo", { photo_url: reader.result });
                setUser(data);
                toast.success("Photo updated");
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Could not upload");
            } finally { setPhotoUploading(false); }
        };
        reader.readAsDataURL(f);
    };

    const openConsult = async (cid) => {
        try {
            const { data } = await api.get(`/consultations/${cid}`);
            setActive(data);
        } catch (e) {
            toast.error("Could not open consult");
        }
    };

    const score = data?.user?.health_score ?? user?.health_score ?? 0;
    const vitals = data?.latest_vitals;
    const history = data?.history || [];
    const activeConsults = useMemo(
        () => (consults || []).filter((c) => c.status === "active"),
        [consults],
    );

    const trendData = useMemo(() => {
        return [...history].reverse().map((h, i) => ({
            n: i + 1,
            score: h.health_score,
            bp: h.blood_pressure_systolic,
            spo2: h.spo2,
            hr: h.heart_rate,
            bmi: h.bmi,
            temp: h.body_temperature,
            ts: new Date(h.captured_at).toLocaleDateString(),
        }));
    }, [history]);

    return (
        <div className="flex bg-[var(--medx-bg-soft)] min-h-screen pb-20 md:pb-0" data-testid="patient-dashboard">
            <div className="hidden md:block">
                <DashSidebar items={localizedFullItems} basePath="/dashboard" title="Patient" />
            </div>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-b border-gray-100 px-4 h-14 flex items-center justify-between gap-2" data-testid="mobile-top-bar">
                <button
                    onClick={() => setDrawerOpen(true)}
                    className="w-10 h-10 rounded-xl hover:bg-gray-100 grid place-items-center text-gray-700 shrink-0"
                    aria-label="Open menu"
                    data-testid="mobile-menu-btn"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className={`w-9 h-9 rounded-full grid place-items-center text-white text-xs font-bold overflow-hidden shrink-0 ${activeDependent ? "bg-gradient-to-br from-emerald-500 to-emerald-700" : "bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)]"}`}>
                        {activeDependent
                            ? (activeDependent.first_name || "D").slice(0, 1).toUpperCase()
                            : (user?.photo_url ? <img src={user.photo_url} alt="" className="w-full h-full object-cover" /> : (user?.name || "U").slice(0, 1).toUpperCase())}
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] text-gray-500 leading-none truncate">
                            {activeDependent ? "Dependent" : "Hi,"}
                        </div>
                        <div className="text-sm font-semibold leading-none mt-0.5 truncate">
                            {activeDependent ? activeDependent.first_name : (user?.first_name || (user?.name || "").split(" ")[0])}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {dependents.filter((d) => d.status === "approved").length > 0 && (
                        <ProfileSwitcher
                            parentUser={user}
                            dependents={dependents}
                            activeDependentId={activeDependentId}
                            onChange={setActiveDependentId}
                        />
                    )}
                    <button onClick={openUpload} className="text-xs btn-primary !py-2 !px-3" data-testid="upload-scan-btn-mobile">
                        <UploadCloud className="w-3 h-3 inline mr-1" /> Upload
                    </button>
                </div>
            </div>

            {/* Mobile slide-out drawer with the FULL sidebar (all pages reachable) */}
            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                items={localizedFullItems}
                basePath="/dashboard"
                user={user}
                onSignOut={logout}
            />

            <main className="flex-1 p-4 pt-20 md:p-10 min-w-0">
                {/* Desktop header */}
                <div className="hidden md:flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-1">
                            {activeDependent ? `${activeDependent.name.toUpperCase()}'S DASHBOARD · DEPENDENT` : "PATIENT DASHBOARD"}
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                            {activeDependent
                                ? `Hi, here's ${activeDependent.first_name}'s health today.`
                                : `Hi ${user?.first_name || user?.name?.split(" ")[0]}, here's your health today.`}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <ProfileSwitcher
                            parentUser={user}
                            dependents={dependents}
                            activeDependentId={activeDependentId}
                            onChange={setActiveDependentId}
                        />
                        <button onClick={openUpload} className="btn-primary inline-flex items-center gap-2" data-testid="upload-scan-btn">
                            <UploadCloud className="w-4 h-4" /> Upload a Scan
                        </button>
                    </div>
                </div>

                {tab === "overview" && (
                    <div className="space-y-8" data-testid="tab-overview">
                        {canAccessDoctors && activeConsults.length > 0 && (
                            <div className="rounded-3xl p-5 md:p-6 border-2 border-emerald-200 bg-emerald-50/80 flex items-center gap-4 flex-wrap" data-testid="overview-consult-banner">
                                <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0 bg-emerald-600 text-white">
                                    <MessagesSquare className="w-6 h-6" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] font-mono tracking-[0.22em] opacity-70 text-emerald-900">ACTIVE CONSULTATION{activeConsults.length > 1 ? "S" : ""}</div>
                                    <div className="font-display text-lg font-bold text-emerald-900 truncate">
                                        {activeConsults.length === 1
                                            ? `${activeConsults[0].doctor_name} · ${activeConsults[0].doctor_speciality || "Doctor"}`
                                            : `${activeConsults.length} consultations in progress`}
                                    </div>
                                    <div className="text-sm text-emerald-800/80 truncate">
                                        {activeConsults.length === 1 ? activeConsults[0].problem : "Pick one from your Consultations tab."}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        if (activeConsults.length === 1) openConsult(activeConsults[0].id);
                                        else window.location.hash = "#consultations";
                                    }}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 shadow"
                                    data-testid="overview-consult-resume"
                                >
                                    Resume <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {vitals?.hr_flag && vitals.hr_flag.code !== "ok" && (
                            <div
                                className={`rounded-3xl p-5 md:p-6 border-2 flex items-center gap-4 ${
                                    vitals.hr_flag.code === "urgent_leave"
                                        ? "bg-red-50 border-red-200 text-red-900"
                                        : "bg-amber-50 border-amber-200 text-amber-900"
                                }`}
                                data-testid={`overview-hr-flag-${vitals.hr_flag.code}`}
                            >
                                <div className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${vitals.hr_flag.code === "urgent_leave" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                                    <Sparkles className="w-6 h-6" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[10px] font-mono tracking-[0.22em] opacity-70">TRIAGE FLAG</div>
                                    <div className="font-display text-xl font-bold">{vitals.hr_flag.label}</div>
                                    <div className="text-sm opacity-90">{vitals.hr_flag.message}</div>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                            <div className="lg:col-span-5 card-soft p-8 flex flex-col items-center justify-center relative overflow-hidden">
                                <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-gradient-to-br from-[var(--medx-red)]/10 to-[var(--medx-orange)]/10 blur-3xl" />
                                <HealthScoreHalo score={score} size={300} />
                                <div className="mt-6 text-sm text-gray-600 max-w-xs text-center">
                                    {score === 0
                                        ? "Your Health Score will glow brighter with every kiosk scan."
                                        : `Updated ${vitals ? new Date(vitals.captured_at).toLocaleString() : "now"}.`}
                                </div>
                                {vitals && (
                                    <div className="mt-5 w-full flex justify-center">
                                        <WhatsAppShareButton user={user} vitals={vitals} label="Send to WhatsApp" />
                                    </div>
                                )}
                            </div>
                            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
                                {!vitals ? (
                                    <div className="col-span-2"><EmptyState onUpload={openUpload} /></div>
                                ) : (
                                    <>
                                        <VitalCard icon={Heart} label="Blood Pressure" value={`${vitals.blood_pressure_systolic}/${vitals.blood_pressure_diastolic}`} unit="mmHg" />
                                        <VitalCard icon={Activity} label="Heart Rate" value={vitals.heart_rate} unit="bpm" />
                                        <VitalCard icon={Droplet} label="SpO₂" value={vitals.spo2} unit="%" />
                                        <VitalCard icon={Thermometer} label="Body Temp" value={vitals.body_temperature} unit="°C" />
                                        <VitalCard icon={Scale} label="BMI" value={vitals.bmi} />
                                        <VitalCard icon={Wind} label="Resp Rate" value={vitals.respiratory_rate} unit="/min" />
                                    </>
                                )}
                            </div>
                        </div>

                        {trendData.length > 1 && (
                            <div className="card-soft p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <div className="text-xs font-mono tracking-[0.22em] text-gray-500">HEALTH SCORE TREND</div>
                                        <div className="font-display text-xl font-semibold">Last {trendData.length} scans</div>
                                    </div>
                                </div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={trendData}>
                                            <defs>
                                                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#e63946" stopOpacity={0.4} />
                                                    <stop offset="100%" stopColor="#f77f00" stopOpacity={0.04} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                            <XAxis dataKey="ts" stroke="#9ca3af" fontSize={11} />
                                            <YAxis domain={[40, 100]} stroke="#9ca3af" fontSize={11} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                            <Area type="monotone" dataKey="score" stroke="#e63946" strokeWidth={2.5} fill="url(#grad)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "vitals" && (
                    <div className="space-y-6" data-testid="tab-vitals">
                        {!vitals ? <EmptyState onUpload={openUpload} /> : (
                            <KioskVitalsReport
                                vitals={vitals}
                                patient={user}
                                onRunAiReport={() => {
                                    window.location.hash = "#ai-report";
                                }}
                            />
                        )}
                    </div>
                )}

                {tab === "statistics" && (
                    <div className="space-y-6" data-testid="tab-statistics">
                        <h2 className="font-display text-2xl font-semibold">Statistics</h2>
                        {trendData.length === 0 ? <EmptyState onUpload={openUpload} /> : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="card-soft p-6">
                                    <div className="text-sm font-mono tracking-wider text-gray-500 mb-3">BLOOD PRESSURE</div>
                                    <div className="h-56">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={trendData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                                <XAxis dataKey="ts" stroke="#9ca3af" fontSize={11} />
                                                <YAxis stroke="#9ca3af" fontSize={11} />
                                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                                <Bar dataKey="bp" fill="#e63946" radius={[8, 8, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                                <div className="card-soft p-6">
                                    <div className="text-sm font-mono tracking-wider text-gray-500 mb-3">SpO₂ & HEART RATE</div>
                                    <div className="h-56">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RC_Line data={trendData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                                <XAxis dataKey="ts" stroke="#9ca3af" fontSize={11} />
                                                <YAxis stroke="#9ca3af" fontSize={11} />
                                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                                <Line type="monotone" dataKey="spo2" stroke="#f77f00" strokeWidth={2.5} dot={false} />
                                                <Line type="monotone" dataKey="hr" stroke="#e63946" strokeWidth={2.5} dot={false} />
                                            </RC_Line>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "analytics" && (
                    <div className="space-y-6" data-testid="tab-analytics">
                        <h2 className="font-display text-2xl font-semibold">Analytics</h2>
                        {trendData.length === 0 ? <EmptyState onUpload={openUpload} /> : (
                            <div className="card-soft p-6">
                                <div className="text-sm font-mono tracking-wider text-gray-500 mb-3">BMI & TEMPERATURE OVER TIME</div>
                                <div className="h-72">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RC_Line data={trendData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                            <XAxis dataKey="ts" stroke="#9ca3af" fontSize={11} />
                                            <YAxis stroke="#9ca3af" fontSize={11} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                            <Line type="monotone" dataKey="bmi" stroke="#e63946" strokeWidth={2.5} dot={false} name="BMI" />
                                            <Line type="monotone" dataKey="temp" stroke="#f77f00" strokeWidth={2.5} dot={false} name="Temp" />
                                        </RC_Line>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "history" && (
                    // Backwards-compat: old #history links now go to medical-records
                    <MedicalRecords patient={user} />
                )}

                {tab === "medical-records" && (
                    <MedicalRecords patient={user} />
                )}

                {tab === "ai-report" && (
                    <AIValuationReport canGenerate={!!vitals} />
                )}

                {tab === "reports" && (
                    <div className="space-y-6" data-testid="tab-reports">
                        <h2 className="font-display text-2xl font-semibold">Reports</h2>
                        <div className="card-soft p-8 text-center">
                            <FileText className="w-10 h-10 text-[var(--medx-orange)] mx-auto mb-3" />
                            <div className="font-display text-xl font-semibold mb-1">PDF reports — coming soon</div>
                            <p className="text-gray-600 text-sm max-w-md mx-auto">Download a clinical-grade summary you can share with your doctor in seconds.</p>
                        </div>
                    </div>
                )}

                {tab === "scans" && (
                    <ScansTab
                        onOpenUpload={openUpload}
                        dependentId={activeDependentId}
                        dependentName={activeDependent?.first_name}
                    />
                )}

                {tab === "family" && (
                    <FamilyTab
                        dependents={dependents}
                        reload={loadDependents}
                        parentUser={user}
                        parentVitals={data?.latest_vitals}
                    />
                )}

                {tab === "doctors" && (
                    <div className="space-y-6" data-testid="tab-doctors">
                        {canAccessDoctors ? (
                            <OnlineDoctors onConsultationCreated={async (c) => { setActive(c); loadConsults(); }} />
                        ) : (
                            <DoctorsOfflinePanel message={capabilities?.doctors_offline_message} />
                        )}
                    </div>
                )}

                {tab === "consultations" && (
                    <div className="space-y-6" data-testid="tab-consultations">
                        {!canAccessDoctors && consults.length === 0 ? (
                            <DoctorsOfflinePanel message={capabilities?.doctors_offline_message} />
                        ) : (
                            <>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <h2 className="font-display text-2xl font-semibold">Your Consultations</h2>
                                <p className="text-sm text-gray-500 mt-1">Jump back into an active chat with your doctor, or review past consults.</p>
                            </div>
                            {canAccessDoctors && (
                                <button
                                    onClick={() => { window.location.hash = "#doctors"; }}
                                    className="btn-outline inline-flex items-center gap-2 text-sm"
                                    data-testid="consultations-find-doctor"
                                >
                                    <MessageCircle className="w-4 h-4" /> Find a doctor
                                </button>
                            )}
                        </div>

                        {consults.length === 0 ? (
                            <div className="card-soft p-10 text-center" data-testid="consultations-empty">
                                <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 grid place-items-center mb-4">
                                    <MessagesSquare className="w-7 h-7 text-[var(--medx-orange)]" />
                                </div>
                                <div className="font-display text-2xl font-semibold mb-2">No consultations yet</div>
                                <p className="text-gray-600 max-w-md mx-auto mb-6">Start a free consult with one of our online doctors — your vitals come along automatically.</p>
                                {canAccessDoctors && (
                                    <button
                                        onClick={() => { window.location.hash = "#doctors"; }}
                                        className="btn-primary inline-flex items-center gap-2"
                                        data-testid="consultations-browse-doctors"
                                    >
                                        Browse doctors <ArrowRight className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {consults.map((c) => {
                                    const isActive = c.status === "active";
                                    return (
                                        <div key={c.id} className={`card-soft p-5 flex items-center gap-4 flex-wrap ${isActive ? "border-emerald-200 ring-1 ring-emerald-200/50" : ""}`} data-testid={`consult-card-${c.id}`}>
                                            <div className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${isActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                                                <MessagesSquare className="w-6 h-6" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="font-semibold text-gray-900 truncate">{c.doctor_name}</div>
                                                    <span className="text-xs text-gray-500">· {c.doctor_speciality || "Doctor"}</span>
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono tracking-wider ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid={`consult-status-${c.id}`}>
                                                        {(c.status || "active").toUpperCase()}
                                                    </span>
                                                    {c.vitals_access_granted ? (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-mono">VITALS SHARED</span>
                                                    ) : (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-mono">PIN {c.access_pin}</span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-600 mt-1 truncate max-w-2xl">{c.problem}</div>
                                                <div className="text-xs text-gray-400 mt-1">Started {new Date(c.created_at).toLocaleString()}</div>
                                            </div>
                                            <button
                                                onClick={() => openConsult(c.id)}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${isActive ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border border-gray-200 text-gray-700 hover:bg-gray-50"}`}
                                                data-testid={`consult-resume-${c.id}`}
                                            >
                                                {isActive ? "Resume" : "Open"} <ArrowRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                            </>
                        )}
                    </div>
                )}

                {tab === "settings" && (
                    <div className="space-y-6" data-testid="tab-settings">
                        <h2 className="font-display text-2xl font-semibold">Profile &amp; Settings</h2>
                        <div className="card-soft p-7">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-6">
                                <div className="relative shrink-0">
                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white font-bold text-2xl overflow-hidden border-4 border-white shadow-lg">
                                        {user?.photo_url ? <img src={user.photo_url} alt="profile" className="w-full h-full object-cover" data-testid="profile-photo-img" /> : (user?.name || "U").slice(0, 1).toUpperCase()}
                                    </div>
                                    <label className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white border border-gray-200 grid place-items-center cursor-pointer shadow hover:bg-orange-50" data-testid="profile-photo-trigger">
                                        <Camera className="w-4 h-4 text-[var(--medx-red)]" />
                                        <input type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={photoUploading} data-testid="profile-photo-input" />
                                    </label>
                                </div>
                                <div>
                                    <div className="font-display text-xl font-semibold">{user?.name}</div>
                                    <div className="text-sm text-gray-500">{user?.email}</div>
                                    <div className="text-xs text-gray-400 mt-1">{photoUploading ? "Uploading…" : "Click the camera to upload a profile photo (max 2 MB)"}</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm border-t border-gray-100 pt-5">
                                <div>
                                    <div className="text-xs font-mono tracking-wider text-gray-500">FIRST NAME</div>
                                    <div className="font-medium">{user?.first_name || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-mono tracking-wider text-gray-500">SURNAME</div>
                                    <div className="font-medium">{user?.last_name || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-mono tracking-wider text-gray-500">AGE</div>
                                    <div className="font-medium">{user?.age || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-mono tracking-wider text-gray-500">COUNTRY</div>
                                    <div className="font-medium">{user?.country || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-mono tracking-wider text-gray-500">MOBILE</div>
                                    <div className="font-medium">{user?.country_code} {user?.mobile || user?.phone || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-mono tracking-wider text-gray-500">MEMBER SINCE</div>
                                    <div className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-5 mt-5">
                                <div className="text-xs font-mono tracking-wider text-gray-500 flex items-center gap-2 mb-2">
                                    <UserIcon className="w-3 h-3 text-[var(--medx-orange)]" /> BIO
                                </div>
                                <textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)}
                                    placeholder="Tell us a little about yourself — allergies, conditions, preferred language…"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none"
                                    data-testid="profile-bio-input" />
                                <button onClick={async () => {
                                    setSavingBio(true);
                                    try {
                                        // Bio is stored client-side only (no dedicated endpoint) — extend later
                                        setUser((u) => ({ ...u, bio }));
                                        toast.success("Bio saved");
                                    } finally { setSavingBio(false); }
                                }} disabled={savingBio} className="btn-outline mt-3 !py-2 !px-4 text-sm" data-testid="profile-bio-save">
                                    {savingBio ? "Saving…" : "Save bio"}
                                </button>
                            </div>
                        </div>

                        <SettingsSecurity user={user} onUserChange={setUser} />
                    </div>
                )}

                {tab === "_old_settings_unused" && null}

                {loadingData && !data && (
                    <div className="text-center text-gray-500 py-12">Loading…</div>
                )}
            </main>
            <MobileBottomNav items={canAccessDoctors ? ITEMS : ITEMS.filter((i) => i.id !== "consultations")} basePath="/dashboard" />
            {uploadOpen && (
                <ScanUploadModal
                    dependentId={activeDependentId}
                    dependentName={activeDependent?.name}
                    onClose={() => setUploadOpen(false)}
                    onUploaded={() => { setUploadOpen(false); load({ silent: true }); }}
                />
            )}
            {active && (
                <ConsultationChat
                    consultation={active}
                    currentRole="patient"
                    onClose={() => { setActive(null); loadConsults(); }}
                    onConsultationUpdated={async () => {
                        const { data } = await api.get(`/consultations/${active.id}`);
                        setActive(data);
                        loadConsults();
                    }}
                />
            )}
            <DoctorAccessRequestPopup onConsultationCreated={(c) => { if (canAccessDoctors) { setActive(c); loadConsults(); } }} />
        </div>
    );
}


/* ================================================================
 * SettingsSecurity — password change, email/mobile/ID request with
 * email OTP, and permanent delete request (admin-approved).
 * ================================================================ */
function SettingsSecurity({ user, onUserChange }) {
    const [requests, setRequests] = useState([]);
    const loadRequests = async () => {
        try {
            const { data } = await api.get("/me/profile/change-requests");
            setRequests(data);
        } catch { /* silent */ }
    };
    useEffect(() => { loadRequests(); }, []);
    return (
        <>
            <ChangePasswordCard />
            <ProfileChangeCard
                kind="email"
                icon={Mail}
                title="Email address"
                current={user?.email}
                hint="Changing your email requires OTP verification + admin approval."
                onDone={loadRequests}
            />
            <ProfileChangeCard
                kind="mobile"
                icon={Phone}
                title="Mobile number"
                current={`${user?.country_code || ""} ${user?.mobile || user?.phone || ""}`.trim() || "—"}
                hint="Changing your mobile requires OTP verification + admin approval."
                onDone={loadRequests}
            />
            <ProfileChangeCard
                kind="id_number"
                icon={IdCard}
                title="SA ID number"
                current={user?.id_number || "Not yet added"}
                hint="Add or update your 13-digit SA ID (one ID per Medx AI account — anti-abuse)."
                onDone={loadRequests}
            />
            <DeleteAccountCard onDone={loadRequests} />
            <PendingRequestsCard requests={requests} />
            <ComplianceCard />
        </>
    );
}

function ChangePasswordCard() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
        if (next !== confirm) { toast.error("New passwords do not match"); return; }
        setBusy(true);
        try {
            await api.post("/me/password/change", { current_password: current, new_password: next });
            toast.success("Password updated. We've emailed you a confirmation.");
            setCurrent(""); setNext(""); setConfirm("");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update password");
        } finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} className="card-soft p-7 space-y-4" data-testid="settings-password-card">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 grid place-items-center text-[var(--medx-orange)]">
                    <Lock className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="font-display text-lg font-semibold">Change password</h3>
                    <p className="text-xs text-gray-500">Self-service. We'll email a confirmation to your inbox.</p>
                </div>
            </div>
            <div className="grid gap-3">
                <input type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)}
                    required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                    data-testid="settings-current-password" />
                <input type="password" placeholder="New password (min 8 chars)" value={next} onChange={(e) => setNext(e.target.value)}
                    required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                    data-testid="settings-new-password" />
                <input type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                    data-testid="settings-confirm-password" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="settings-password-submit">
                {busy ? "Updating…" : "Update password"}
            </button>
        </form>
    );
}

function ProfileChangeCard({ kind, icon: Icon, title, current, hint, onDone }) {
    const [stage, setStage] = useState("idle");
    const [newValue, setNewValue] = useState("");
    const [countryCode, setCountryCode] = useState("+27");
    const [current_password, setCurrentPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [reqId, setReqId] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = {
                change_type: kind,
                new_value: newValue,
                current_password,
                ...(kind === "mobile" ? { new_country_code: countryCode } : {}),
            };
            const { data } = await api.post("/me/profile/request-change", body);
            setReqId(data.request_id);
            setStage("pending_otp");
            toast.success(data.message || "OTP sent to your email");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
        } finally { setBusy(false); }
    };

    const verify = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/me/profile/verify-otp", { request_id: reqId, otp });
            toast.success(data.message || "Verified. Awaiting admin approval.");
            setStage("idle"); setNewValue(""); setOtp(""); setCurrentPassword(""); setReqId(null);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
        } finally { setBusy(false); }
    };

    const placeholder = kind === "email" ? "new@email.com" : kind === "mobile" ? "71 555 0123" : "0000000000000";
    return (
        <div className="card-soft p-7 space-y-4" data-testid={`settings-${kind}-card`}>
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 grid place-items-center text-[var(--medx-orange)]">
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-semibold">{title}</h3>
                    <p className="text-xs text-gray-500 truncate">Current: <span className="font-mono text-gray-700">{current || "—"}</span></p>
                </div>
            </div>
            <p className="text-xs text-gray-500">{hint}</p>

            {stage === "idle" && (
                <form onSubmit={submit} className="grid gap-3">
                    {kind === "mobile" ? (
                        <div className="flex gap-2">
                            <input value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                                className="w-20 px-3 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm font-mono"
                                data-testid={`settings-${kind}-cc`} />
                            <input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={placeholder}
                                required className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                data-testid={`settings-${kind}-new`} />
                        </div>
                    ) : (
                        <input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={placeholder}
                            required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                            type={kind === "email" ? "email" : "text"}
                            data-testid={`settings-${kind}-new`} />
                    )}
                    <input type="password" value={current_password} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Confirm with current password"
                        required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                        data-testid={`settings-${kind}-pwd`} />
                    <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60 w-fit" data-testid={`settings-${kind}-submit`}>
                        {busy ? "Submitting…" : "Request change"}
                    </button>
                </form>
            )}
            {stage === "pending_otp" && (
                <form onSubmit={verify} className="grid gap-3">
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                        We've emailed a 6-digit OTP to your <b>current</b> email. Enter it to confirm the request.
                    </div>
                    <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code"
                        required maxLength={6}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm font-mono tracking-widest text-center"
                        data-testid={`settings-${kind}-otp`} />
                    <div className="flex gap-2">
                        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid={`settings-${kind}-verify`}>
                            {busy ? "Verifying…" : "Verify OTP"}
                        </button>
                        <button type="button" onClick={() => setStage("idle")} className="btn-outline text-sm">Cancel</button>
                    </div>
                </form>
            )}
        </div>
    );
}

function DeleteAccountCard({ onDone }) {
    const [stage, setStage] = useState("idle");
    const [reason, setReason] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [reqId, setReqId] = useState(null);
    const [busy, setBusy] = useState(false);

    const request = async (e) => {
        e.preventDefault();
        if (!window.confirm("Permanently delete your Medx AI account? This cannot be undone once an admin approves it.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/me/profile/request-change", {
                change_type: "delete", current_password: password, reason,
            });
            setReqId(data.request_id);
            setStage("pending_otp");
            toast.success(data.message || "OTP sent");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Request failed");
        } finally { setBusy(false); }
    };
    const verify = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/me/profile/verify-otp", { request_id: reqId, otp });
            toast.success("Verified — an admin will finalise your account closure.");
            setStage("idle"); setPassword(""); setOtp(""); setReason(""); setReqId(null);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="card-soft p-7 border-red-200 bg-red-50/40" data-testid="settings-delete-card">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 grid place-items-center text-red-600"><Trash2 className="w-5 h-5" /></div>
                <div>
                    <h3 className="font-display text-lg font-semibold text-red-900">Delete my account permanently</h3>
                    <p className="text-xs text-red-900/80">POPIA / GDPR right-to-erasure. OTP verified, admin reviewed, then permanently anonymised.</p>
                </div>
            </div>
            {stage === "idle" ? (
                <form onSubmit={request} className="grid gap-3">
                    <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional — helps us improve)"
                        className="w-full px-4 py-3 rounded-xl border border-red-200 focus:border-red-400 outline-none text-sm resize-none bg-white"
                        data-testid="settings-delete-reason" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="Confirm with your password" required
                        className="w-full px-4 py-3 rounded-xl border border-red-200 focus:border-red-400 outline-none text-sm bg-white"
                        data-testid="settings-delete-password" />
                    <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60 w-fit"
                        data-testid="settings-delete-submit">
                        <Trash2 className="w-4 h-4" /> {busy ? "Submitting…" : "Request account deletion"}
                    </button>
                </form>
            ) : (
                <form onSubmit={verify} className="grid gap-3">
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                        A 6-digit OTP has been emailed to your current address. Enter it to confirm.
                    </div>
                    <input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} required placeholder="6-digit code"
                        className="w-full px-4 py-3 rounded-xl border border-red-200 focus:border-red-400 outline-none text-sm font-mono tracking-widest text-center bg-white"
                        data-testid="settings-delete-otp" />
                    <div className="flex gap-2">
                        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="settings-delete-verify">
                            {busy ? "Verifying…" : "Verify & submit"}
                        </button>
                        <button type="button" onClick={() => setStage("idle")} className="btn-outline text-sm">Cancel</button>
                    </div>
                </form>
            )}
        </div>
    );
}

function PendingRequestsCard({ requests }) {
    if (!requests || requests.length === 0) return null;
    const label = { email: "Email address", mobile: "Mobile number", id_number: "ID number", delete: "Account deletion" };
    const statusPill = (s) => {
        if (s === "pending_otp") return <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-800">OTP PENDING</span>;
        if (s === "pending_admin") return <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-blue-100 text-blue-800">AWAITING ADMIN</span>;
        if (s === "approved") return <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">APPROVED</span>;
        if (s === "rejected") return <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-gray-100 text-gray-700">REJECTED</span>;
        return <span className="text-[10px] font-mono tracking-wider px-2 py-1 rounded-full bg-gray-100 text-gray-700">{s?.toUpperCase()}</span>;
    };
    return (
        <div className="card-soft p-7" data-testid="settings-pending-card">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 grid place-items-center text-blue-600"><ClockIcon className="w-5 h-5" /></div>
                <h3 className="font-display text-lg font-semibold">Your change requests</h3>
            </div>
            <ul className="divide-y divide-gray-100">
                {requests.map((r) => (
                    <li key={r.id} className="py-3 flex items-center gap-3 flex-wrap text-sm" data-testid={`settings-request-${r.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium">{label[r.change_type] || r.change_type}</div>
                            {r.new_value && <div className="text-xs text-gray-500 font-mono truncate">{r.new_value}</div>}
                        </div>
                        {statusPill(r.status)}
                        <div className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ComplianceCard() {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white/60 p-6 text-xs text-gray-600 leading-relaxed" data-testid="settings-compliance-card">
            <div className="flex items-center gap-2 mb-2 text-gray-900 font-semibold">
                <Shield className="w-4 h-4 text-[var(--medx-orange)]" /> Your rights under POPIA & HIPAA
            </div>
            Medx AI stores your personal and health information under the SA Protection of Personal Information Act (POPIA) and
            — for US-serving pilots — the Health Insurance Portability and Accountability Act (HIPAA). You can:
            <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Request a copy of your data at any time (<a className="underline" href="mailto:privacy@medxai.online">privacy@medxai.online</a>).</li>
                <li>Correct inaccurate records via the forms above — admin verified.</li>
                <li>Permanently delete your account (right to erasure).</li>
                <li>Withdraw consent for research / analytics in your Settings.</li>
            </ul>
            <div className="mt-3 text-[11px] text-gray-500">Anti-abuse: email / mobile / ID numbers are enforced unique across accounts. Repeated change requests are rate-limited.</div>
        </div>
    );
}

/* ================================================================
 * ScanUploadModal — patient uploads a photo of a printed Medx AI slip.
 * ================================================================ */
function ScanUploadModal({ onClose, onUploaded, dependentId = null, dependentName = null }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notes, setNotes] = useState("");
    const [storage, setStorage] = useState(null);
    // One-time photography guide — auto-opens for first-time uploaders.
    const [guideOpen, setGuideOpen] = useState(() => shouldShowScanGuide());

    useEffect(() => {
        api.get("/me/scans/storage").then(({ data }) => setStorage(data)).catch(() => {});
    }, []);

    const onFile = (f) => {
        if (!f) return;
        const mime = (f.type || "").toLowerCase();
        // Phones often return HEIC/HEIF (iPhone) or WEBP. Backend + Gemini vision
        // handle all of these. We only block obviously-wrong types (PDF, zip, etc.)
        const ok = mime.startsWith("image/") || /\.(png|jpe?g|heic|heif|webp)$/i.test(f.name || "");
        if (!ok) {
            toast.error("Please choose a photo (PNG, JPEG, HEIC or WEBP).");
            return;
        }
        if (f.size > 15_000_000) { toast.error("Please pick an image under 15 MB"); return; }
        setFile(f);
        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result);
        reader.onerror = () => toast.error("Could not read that file — try another photo.");
        reader.readAsDataURL(f);
    };

    const submit = async () => {
        if (!preview) { toast.error("Pick a photo first"); return; }
        setBusy(true);
        try {
            // Resolve MIME: iPhone sometimes gives empty type for HEIC — infer from extension.
            let mime = (file?.type || "").toLowerCase();
            if (!mime && file?.name) {
                const ext = file.name.split(".").pop()?.toLowerCase();
                if (ext === "heic") mime = "image/heic";
                else if (ext === "heif") mime = "image/heif";
                else if (ext === "webp") mime = "image/webp";
                else if (ext === "png") mime = "image/png";
                else mime = "image/jpeg";
            }
            const { data } = await api.post("/me/scans/upload", {
                image_base64: preview,
                mime_type: mime || "image/jpeg",
                notes: notes || undefined,
                dependent_id: dependentId || undefined,
            });
            toast.success(data.message || "Successfully uploaded.", { duration: 6000 });
            onUploaded?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed", { duration: 7000 });
        } finally { setBusy(false); }
    };

    const usedMB = storage ? (storage.used_bytes / (1024 * 1024)).toFixed(1) : null;
    const quotaMB = storage ? storage.quota_mb : 500;
    const pct = storage ? Math.min(100, (storage.used_bytes / storage.quota_bytes) * 100) : 0;

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="scan-upload-modal">
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                style={{ maxHeight: "90vh" }}
            >
                {/* Pinned header */}
                <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
                    <div className="min-w-0">
                        <h3 className="font-display text-xl font-semibold">
                            {dependentName ? `Upload a scan for ${dependentName}` : "Upload your assessment slip"}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPEG, HEIC or WEBP · AI validates 30+ Medx AI markers.</p>
                        <button
                            type="button"
                            onClick={() => setGuideOpen(true)}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--medx-red)] hover:underline"
                            data-testid="scan-upload-photo-tips"
                        >
                            <Camera className="w-3 h-3" /> View photo tips
                        </button>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500 shrink-0" aria-label="Close">✕</button>
                </div>
                {/* Scrollable body */}
                <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4" data-testid="scan-upload-scroll-body">
                    {dependentName && (
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900" data-testid="scan-upload-dependent-banner">
                            Uploading on behalf of <b>{dependentName}</b>. The scan will appear in their profile only.
                        </div>
                    )}
                    {storage && (
                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-3" data-testid="scan-upload-quota">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                                <span>File storage</span>
                                <span className="font-mono">{usedMB} / {quotaMB} MB</span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    )}
                    <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center" data-testid="scan-upload-dropzone">
                        {preview ? (
                            <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                        ) : (
                            <>
                                <UploadCloud className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                                <div className="font-medium text-gray-700">Choose a photo of your Medx AI slip</div>
                                <div className="text-xs text-gray-500 mt-1">PNG, JPEG, HEIC or WEBP · up to 15 MB</div>
                            </>
                        )}
                        <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                            <label htmlFor="scan-file-gallery" className="btn-outline inline-flex items-center justify-center gap-2 cursor-pointer text-sm" data-testid="scan-upload-gallery-btn">
                                <UploadCloud className="w-4 h-4" /> Upload from gallery
                                <input id="scan-file-gallery" type="file" accept="image/*,.heic,.heif"
                                    className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="scan-upload-input-gallery" />
                            </label>
                            <label htmlFor="scan-file-camera" className="btn-outline inline-flex items-center justify-center gap-2 cursor-pointer text-sm" data-testid="scan-upload-camera-btn">
                                <Camera className="w-4 h-4" /> Take a photo
                                <input id="scan-file-camera" type="file" accept="image/*" capture="environment"
                                    className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="scan-upload-input-camera" />
                            </label>
                        </div>
                    </div>
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add a note (optional)"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none"
                        data-testid="scan-upload-notes" />
                </div>
                {/* Pinned footer — submit button is always visible */}
                <div className="p-4 border-t border-gray-100 bg-white flex justify-end gap-2 shrink-0">
                    <button onClick={onClose} className="btn-outline text-sm" disabled={busy}>Cancel</button>
                    <button onClick={submit} disabled={busy || !preview} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="scan-upload-submit">
                        <UploadCloud className="w-4 h-4" /> {busy ? "Scanning slip…" : "Upload & analyse"}
                    </button>
                </div>
            </div>
            {guideOpen && (
                <ScanPhotoGuideModal
                    onClose={() => setGuideOpen(false)}
                    onGotIt={() => setGuideOpen(false)}
                />
            )}
        </div>
    );
}
